import * as React from 'react';

import { LucideProps } from 'lucide-react';
import { Button as AriaButton, PressEvent } from 'react-aria-components';

import { cn, withColor } from '../../../lib/stylingUtils';
import { ThemeColor } from '../../../types/theme';

export interface IconButtonProps {
  /**
   * The icon element to display within the button.
   * Must be a Lucide icon component.
   */
  icon: React.ReactElement<LucideProps>;

  /**
   * Optional aria-label for accessibility.
   * Required if the icon doesn't have a clear meaning.
   */
  'aria-label'?: string;

  /**
   * Optional className for custom styling
   */
  className?: string;

  /**
   * Whether the button is disabled
   */
  isDisabled?: boolean;

  /**
   * Callback when the button is pressed
   */
  onPress?: (e: PressEvent) => void;

  onPressStart?: (e: PressEvent) => void;

  onPressEnd?: (e: PressEvent) => void;

  /**
   * The visual style of the button
   */
  variant?: 'solid' | 'ghost' | 'outline' | 'text' | 'unstyled';

  /**
   * Theme color to apply to the button.
   * Controls the background, border, and text colors.
   */
  color?: ThemeColor;
}

/**
 * IconButton component specifically designed for icon-only buttons.
 * Built on top of react-aria-components for accessibility.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <IconButton icon={<PlusIcon />} aria-label="Add item" />
 *
 * // With variant and color
 * <IconButton
 *   icon={<TrashIcon />}
 *   variant="outline"
 *   color="failure"
 *   aria-label="Delete"
 * />
 * ```
 */
export const IconButton = ({
  icon,
  variant = 'ghost',
  color,
  className,
  ...props
}: IconButtonProps) => (
  <AriaButton
    {...props}
    style={withColor(color)}
    preventFocusOnPress
    // prettier-ignore
    className={cn(
        '@IconButton',
        'inline-flex items-center justify-center size-8 rounded-md transition-colors cursor-pointer',
        variant === 'ghost'  && !color &&  'hover:bg-neutral/10 active:bg-neutral/20',
        variant === 'ghost' && color && 'text-custom-color hover:bg-custom-color/10 active:bg-custom-color/20',
        variant === 'solid' && color && 'bg-custom-color rac-hover:bg-custom-color/80 active:bg-custom-color text-white',
        variant === 'solid' && !color && 'bg-primary rac-hover:bg-primary/80 active:bg-primary-darker text-white',
        variant === 'solid' && color === 'white' && 'bg-white rac-hover:bg-neutral/10 active:bg-neutral/75 text-neutral border-neutral/10 border',
        variant === 'outline' && color && 'border-custom-color text-custom-color rac-hover:border-custom-color/80 rac-hover:text-custom-color/80 active:border-custom-color active:text-custom-color border',
        variant === 'outline' && !color && 'border-neutral/20 text-neutral hover:border-neutral/40 hover:text-neutral/70 active:border-neutral/60 active:text-neutral/80 border',
        variant === 'text' && 'hover:bg-neutral/10 active:bg-neutral/20',
        'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
  >
    {icon}
  </AriaButton>
);
